import { createContext } from '@lit/context';

import type Person from '../models/person.js';
import type Company from '../models/company.js';
import type { AuthState, LtnAuthService } from '../ltn-auth/ltn-auth-service.js';
import type { LtnRouter, Route } from '../ltn-router/ltn-router.js';
import type { NSSettingsService, SettingsObject } from '../ns-settings/ns-settings-service.js';
import type { LtnToastService } from '../ltn-toast/ltn-toast-service.js';
import type { LtnDialogService } from '../ltn-dialog/ltn-dialog-service.js';
import type { NSDirectoryService } from './ns-directory-service.js';
import type { NSLoader } from './ns-loader-service.js';
import type { NSCommuncationService } from './ns-communication-service.js';
import type { PublicLedgerService } from './ns-public-ledger-service.js';
import type { NSStripeService } from './ns-stripe-service.js';
import type { NSWorkflowService } from '../ns-workflows/ns-workflow-service.js';
import type { TrustScoreSource } from './ns-trust-score-service.js';

// Type-only imports keep this module out of import cycles: @consume reads a context while its class is defined.

export interface DirectoryState {
  me?: Person;
  company?: Company;
  people: Person[];
}

export const nsDirectoryServiceContext = createContext<NSDirectoryService>(Symbol.for('ns.directory-service'));

export const nsDirectoryStateContext = createContext<DirectoryState | undefined>(Symbol.for('ns.directory-state'));

export interface AuthSnapshot {
  state: AuthState;
}

export const ltnAuthServiceContext = createContext<LtnAuthService>(Symbol.for('ns.auth-service'));

export const ltnAuthStateContext = createContext<AuthSnapshot | undefined>(Symbol.for('ns.auth-state'));

export const ltnRouterContext = createContext<LtnRouter>(Symbol.for('ns.router'));

export const ltnRouteContext = createContext<Route | undefined>(Symbol.for('ns.route'));

export const nsSettingsServiceContext = createContext<NSSettingsService>(Symbol.for('ns.settings-service'));

export const nsSettingsContext = createContext<SettingsObject | undefined>(Symbol.for('ns.settings'));

export const ltnToastServiceContext = createContext<LtnToastService>(Symbol.for('ns.toast-service'));

export const ltnDialogServiceContext = createContext<LtnDialogService>(Symbol.for('ns.dialog-service'));

export const nsLoaderContext = createContext<NSLoader>(Symbol.for('ns.loader'));

export const nsCommuncationServiceContext = createContext<NSCommuncationService>(Symbol.for('ns.communcation-service'));

export const publicLedgerServiceContext = createContext<PublicLedgerService>(Symbol.for('ns.public-ledger-service'));

export const nsStripeServiceContext = createContext<NSStripeService>(Symbol.for('ns.stripe-service'));

export const nsWorkflowServiceContext = createContext<NSWorkflowService>(Symbol.for('ns.workflow-service'));

export const nsTrustScoreServiceContext = createContext<TrustScoreSource>(Symbol.for('ns.trust-score-service'));
